import { UserActionDto } from '../../models/dtos/notification/notification-dtos';
import {
  PaymentInvalidation, PaymentProviderInvalidation, RealtimeEnvelope, RegisterInvalidation, TermInvalidation,
  clubUserActionsInvalidation, householdUserActionsInvalidation, paymentInvalidation, paymentProviderInvalidation, realtimeTypes,
  registerInvalidation, termRegistrationInvalidation
} from '../realtime/realtime-message';

// Type IDs are backend-owned. Reuse the existing SignalR IDs rather than creating a second registry.
export const userActionTypes = {
  newRegistrationForClubStaff: '74ea2dc6-808f-4c61-a81f-d2393ed5d63a'
} as const;

export const userActionAudienceTypes = {
  club: 'd98d3063-e8a4-4dee-b172-8216bf27cfaa',
  household: 'b92b51c9-1fc0-4837-a970-24090164b1ec'
} as const;

export interface SystemNotificationPayloads {
  [realtimeTypes.classRegisterChanged]: RegisterInvalidation;
  [realtimeTypes.attendanceExpectationUpdated]: RegisterInvalidation;
  [realtimeTypes.newRegistration]: TermInvalidation;
  [realtimeTypes.registrationUpdated]: TermInvalidation;
  [realtimeTypes.termEmailInvitationChanged]: TermInvalidation;
  [realtimeTypes.paymentStatusUpdated]: PaymentInvalidation;
  [realtimeTypes.paymentProviderConfigUpdated]: PaymentProviderInvalidation;
  [realtimeTypes.paymentProviderExternalAccountSetup]: PaymentProviderInvalidation;
  [realtimeTypes.clubUserActionsChanged]: { clubId: string };
  [realtimeTypes.householdUserActionsChanged]: { householdId: string };
}

export type SystemNotificationType = keyof SystemNotificationPayloads;

export type SystemNotificationOf<T extends SystemNotificationType> = {
  type: T;
  payload: SystemNotificationPayloads[T];
};

export type SystemNotification = {
  [T in SystemNotificationType]: SystemNotificationOf<T>;
}[SystemNotificationType];

// Only a recognised event with a validated, normalised payload enters the typed stream.
// The payload is still an invalidation, never a saved registration, invoice or attendance record.
export function readSystemNotification(envelope: RealtimeEnvelope): SystemNotification | undefined {
  switch (envelope.type)
  {
    case realtimeTypes.classRegisterChanged: {
      const payload = registerInvalidation(envelope);
      return payload ? { type: realtimeTypes.classRegisterChanged, payload } : undefined;
    }

    case realtimeTypes.attendanceExpectationUpdated: {
      const payload = registerInvalidation(envelope);
      return payload ? { type: realtimeTypes.attendanceExpectationUpdated, payload } : undefined;
    }

    case realtimeTypes.newRegistration: {
      const payload = termRegistrationInvalidation(envelope);
      return payload ? { type: realtimeTypes.newRegistration, payload } : undefined;
    }

    case realtimeTypes.registrationUpdated: {
      const payload = termRegistrationInvalidation(envelope);
      return payload ? { type: realtimeTypes.registrationUpdated, payload } : undefined;
    }

    case realtimeTypes.termEmailInvitationChanged: {
      const payload = termRegistrationInvalidation(envelope);
      return payload ? { type: realtimeTypes.termEmailInvitationChanged, payload } : undefined;
    }

    case realtimeTypes.paymentStatusUpdated: {
      const payload = paymentInvalidation(envelope);
      return payload ? { type: realtimeTypes.paymentStatusUpdated, payload } : undefined;
    }

    case realtimeTypes.paymentProviderConfigUpdated: {
      const payload = paymentProviderInvalidation(envelope);
      return payload ? { type: realtimeTypes.paymentProviderConfigUpdated, payload } : undefined;
    }

    case realtimeTypes.paymentProviderExternalAccountSetup: {
      const payload = paymentProviderInvalidation(envelope);
      return payload ? { type: realtimeTypes.paymentProviderExternalAccountSetup, payload } : undefined;
    }

    case realtimeTypes.clubUserActionsChanged: {
      const clubId = clubUserActionsInvalidation(envelope);
      return clubId ? { type: realtimeTypes.clubUserActionsChanged, payload: { clubId } } : undefined;
    }

    case realtimeTypes.householdUserActionsChanged: {
      const householdId = householdUserActionsInvalidation(envelope);
      return householdId ? { type: realtimeTypes.householdUserActionsChanged, payload: { householdId } } : undefined;
    }

    default:
      return undefined;
  }
}

export interface NewRegistrationForClubStaffPayload {
  clubId: string;
  termId: string;
}

export interface SavedUserActionPayloads {
  [userActionTypes.newRegistrationForClubStaff]: NewRegistrationForClubStaffPayload;
}

export type SavedUserActionType = keyof SavedUserActionPayloads;

// A broad saved-action subscriber gets the authoritative API record, not hub-supplied JSON.
// Unknown action types can be observed, but only registered types have decoded payloads.
export interface SavedUserActionNotification {
  type: string;
  action: UserActionDto;
}

export interface SavedUserActionNotificationOf<T extends SavedUserActionType> extends SavedUserActionNotification {
  type: T;
  payload: SavedUserActionPayloads[T];
}

const guidPattern = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const emptyGuid = '00000000-0000-0000-0000-000000000000';

export function readSavedUserActionNotification(action: UserActionDto): SavedUserActionNotification | undefined {
  if (!action || !readGuid(action.id) || !readGuid(action.actionType))
    return undefined;

  return { type: action.actionType.toLowerCase(), action };
}

export function readSavedUserActionOfType<T extends SavedUserActionType>(notification: SavedUserActionNotification, type: T): SavedUserActionNotificationOf<T> | undefined {
  if (notification.type !== type)
    return undefined;

  if (type === userActionTypes.newRegistrationForClubStaff)
  {
    const payload = readNewRegistrationForClubStaff(notification.action);

    if (payload)
      return { ...notification, type, payload: payload as SavedUserActionPayloads[T] };
  }

  return undefined;
}

// This is also the existing Notifications-page route validation contract.
// The saved club ID must match the payload before exposing a typed navigation target.
export function readNewRegistrationForClubStaff(action: UserActionDto): NewRegistrationForClubStaffPayload | undefined {
  if (typeof action.actionType !== 'string' || action.actionType.toLowerCase() !== userActionTypes.newRegistrationForClubStaff ||
      typeof action.audienceType !== 'string' || action.audienceType.toLowerCase() !== userActionAudienceTypes.club)
    return undefined;

  const savedClubId = readGuid(action.clubId);

  if (!savedClubId || typeof action.payloadJson !== 'string' || action.payloadJson.length > 4096)
    return undefined;

  try
  {
    const payload: unknown = JSON.parse(action.payloadJson);

    if (!isRecord(payload))
      return undefined;

    const clubId = readGuid(payload['clubId']);
    const termId = readGuid(payload['termId']);

    return clubId === savedClubId && termId ? { clubId, termId } : undefined;
  }
  catch
  {
    return undefined;
  }
}

function readGuid(value: unknown): string | undefined {
  return typeof value === 'string' && guidPattern.test(value) && value.toLowerCase() !== emptyGuid
    ? value.toLowerCase() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
